import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useNavigate } from 'react-router-dom';
import { getActiveApiUrl } from '../services/api';

const getResumeApiUrl = () => getActiveApiUrl();

const styles = {
  page: {
    minHeight: '100vh',
    background: '#EFF7FF',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    padding: '32px 16px',
    position: 'relative',
    overflow: 'hidden',
  },
  glowEffect: {
    position: 'absolute',
    top: '-50%',
    right: '-30%',
    width: '70%',
    height: '100%',
    background: 'radial-gradient(ellipse, rgba(0,43,91,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  glowEffectLeft: {
    position: 'absolute',
    bottom: '-40%',
    left: '-30%',
    width: '60%',
    height: '80%',
    background: 'radial-gradient(ellipse, rgba(0,43,91,0.05) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  container: {
    maxWidth: 950,
    margin: '0 auto',
    position: 'relative',
    zIndex: 1,
  },

  // Header
  header: {
    textAlign: 'center',
    marginBottom: 40,
    padding: '10px 0',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: '#002B5B',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: 'uppercase',
    padding: '6px 18px',
    borderRadius: 50,
    marginBottom: 16,
    boxShadow: '0 4px 16px rgba(0,43,91,0.25)',
  },
  title: {
    fontSize: 'clamp(32px, 5.5vw, 48px)',
    fontWeight: 800,
    color: '#002B5B',
    margin: '0 0 12px',
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    color: '#0D4C8B',
    fontSize: 16,
    margin: 0,
    fontWeight: 300,
    letterSpacing: '0.3px',
  },

  // Highlighted Project Name
  projectName: {
    display: 'inline-block',
    marginTop: 18,
    padding: '10px 32px',
    background: '#fff',
    border: '2px solid #B3D9F7',
    borderRadius: 50,
    color: '#0D4C8B',
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: '0.5px',
    boxShadow: '0 2px 16px rgba(0,43,91,0.08)',
    position: 'relative',
    overflow: 'hidden',
  },
  projectNameGlow: {
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'radial-gradient(circle, rgba(0,43,91,0.04) 0%, transparent 70%)',
    animation: 'glowPulse 3s ease-in-out infinite',
  },
  projectNameHighlight: {
    color: '#002B5B',
    fontWeight: 800,
    fontSize: '16px',
    position: 'relative',
    zIndex: 1,
  },
  projectNameSpan: {
    color: '#0288D1',
    fontWeight: 600,
    position: 'relative',
    zIndex: 1,
  },
  projectNameDivider: {
    color: '#B3D9F7',
    position: 'relative',
    zIndex: 1,
  },
  projectNameBadge: {
    display: 'inline-block',
    padding: '2px 12px',
    background: '#0288D1',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    borderRadius: 50,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginLeft: 6,
    position: 'relative',
    zIndex: 1,
    boxShadow: '0 2px 10px rgba(2,136,209,0.25)',
  },

  // Card
  card: {
    background: '#fff',
    borderRadius: 20,
    boxShadow: '0 2px 20px rgba(0,0,0,0.07)',
    border: '1px solid #B3D9F7',
    padding: '30px 32px',
    marginBottom: 20,
    transition: 'all 0.3s ease',
  },

  // Back Button
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 28px',
    background: '#002B5B',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginBottom: 20,
    boxShadow: '0 4px 16px rgba(0,43,91,0.25)',
  },

  // Open / Close button
  openBtn: {
    display: 'block',
    width: '100%',
    padding: '18px',
    background: '#002B5B',
    color: '#fff',
    border: 'none',
    borderRadius: 14,
    fontSize: 17,
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.3px',
    transition: 'all 0.3s ease',
    marginBottom: 12,
    boxShadow: '0 4px 16px rgba(0,43,91,0.2)',
  },
  openBtnHint: {
    color: '#0D4C8B',
    fontSize: 13,
    margin: '0 0 4px 0',
    fontWeight: 300,
    letterSpacing: '0.2px',
  },

  // Error
  errorBox: {
    background: '#fff5f5',
    border: '1px solid #ffd0d0',
    borderRadius: 12,
    padding: '14px 20px',
    color: '#e53e3e',
    fontSize: 14,
    marginBottom: 20,
  },

  // History Button - Only shown when logged in
  historyBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    background: '#EFF7FF',
    border: '1px solid #B3D9F7',
    borderRadius: 10,
    color: '#002B5B',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginLeft: 'auto',
  },

  // Upload area
  uploadInfo: {
    background: '#EFF7FF',
    border: '1px solid #B3D9F7',
    borderRadius: 14,
    padding: '18px 22px',
    marginBottom: 18,
  },
  uploadInfoTitle: {
    margin: '0 0 4px',
    fontSize: 15,
    fontWeight: 700,
    color: '#002B5B',
  },
  uploadInfoText: {
    margin: 0,
    fontSize: 13,
    color: '#0D4C8B',
  },
  dropzone: (isDragActive) => ({
    border: `2px dashed ${isDragActive ? '#0288D1' : '#B3D9F7'}`,
    borderRadius: 16,
    padding: '50px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    background: isDragActive ? '#EFF7FF' : '#FAFAFA',
    transition: 'all 0.3s ease',
    marginBottom: 20,
  }),
  dropIcon: {
    fontSize: 44,
    marginBottom: 14,
    opacity: 0.8,
  },
  dropText: {
    color: '#002B5B',
    fontSize: 16,
    margin: '0 0 6px',
    fontWeight: 500,
  },
  dropSub: {
    color: '#0D4C8B',
    fontSize: 13,
    margin: '0 0 12px',
  },
  dropFile: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: '#EFF7FF',
    color: '#002B5B',
    padding: '8px 18px',
    borderRadius: 50,
    fontSize: 13,
    fontWeight: 600,
    marginTop: 8,
    border: '1px solid #B3D9F7',
  },

  // Analyze button
  analyzeBtn: (disabled) => ({
    display: 'block',
    margin: '0 auto 0',
    padding: '16px 48px',
    background: disabled ? '#ddd' : '#002B5B',
    color: disabled ? '#aaa' : '#fff',
    border: 'none',
    borderRadius: 14,
    fontSize: 16,
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    letterSpacing: '0.3px',
    boxShadow: disabled ? 'none' : '0 4px 16px rgba(0,43,91,0.25)',
  }),

  // Spinner
  spinnerWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 14,
    padding: '36px 0',
  },
  spinner: {
    width: 48,
    height: 48,
    border: '3px solid #B3D9F7',
    borderTop: '3px solid #002B5B',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  spinnerText: {
    color: '#002B5B',
    fontSize: 14,
    fontWeight: 500,
  },

  // Section heading
  sectionTitle: (color = '#002B5B') => ({
    fontSize: 17,
    fontWeight: 700,
    color,
    margin: '0 0 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    letterSpacing: '0.2px',
  }),

  // ATS Score
  atsNumber: {
    fontSize: 52,
    fontWeight: 900,
    lineHeight: 1,
  },
  progressTrack: {
    flex: 1,
    height: 10,
    background: '#EFF7FF',
    borderRadius: 99,
    overflow: 'hidden',
    border: '1px solid #B3D9F7',
  },
  progressFill: (pct) => ({
    height: '100%',
    width: `${pct}%`,
    background: '#002B5B',
    borderRadius: 99,
    transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
  }),

  // Grid
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 18,
    marginBottom: 18,
  },

  // Skill tag
  tag: (color = '#002B5B', bg = '#EFF7FF') => ({
    display: 'inline-block',
    padding: '5px 14px',
    background: bg,
    color,
    borderRadius: 50,
    fontSize: 12,
    fontWeight: 600,
    margin: '4px 4px 4px 0',
    border: `1px solid ${color}22`,
    letterSpacing: '0.2px',
  }),

  // Role bar
  roleRow: { marginBottom: 14 },
  roleLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 13,
    color: '#002B5B',
    fontWeight: 500,
    marginBottom: 5,
  },
  rolePct: { color: '#0288D1', fontWeight: 700 },
  roleTrack: {
    height: 7,
    background: '#EFF7FF',
    borderRadius: 99,
    overflow: 'hidden',
    border: '1px solid #B3D9F7',
  },
  roleFill: (pct) => ({
    height: '100%',
    width: `${pct}%`,
    background: '#0288D1',
    borderRadius: 99,
  }),

  // List
  ul: {
    margin: 0,
    paddingLeft: 20,
    color: '#0D4C8B',
    fontSize: 14,
    lineHeight: 1.9,
  },

  // Improved text box
  improvedBox: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 14,
    padding: '20px 24px',
  },
  pre: {
    whiteSpace: 'pre-wrap',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    fontSize: 13.5,
    color: '#166534',
    margin: 0,
    lineHeight: 1.8,
  },

  // History item
  historyItem: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '16px 20px',
    background: '#FAFAFA',
    border: '1px solid #B3D9F7',
    borderRadius: 14,
    cursor: 'pointer',
    marginBottom: 12,
    transition: 'all 0.3s ease',
  },
  historyName: { fontWeight: 700, color: '#002B5B', fontSize: 14, margin: '0 0 4px' },
  historyMeta: { color: '#0D4C8B', fontSize: 13, margin: '0 0 2px' },
  historyDate: { color: '#0D4C8B', fontSize: 12, margin: 0 },

  // Divider row
  flexBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 20,
  },

  // Login prompt for history
  loginPrompt: {
    background: '#EFF7FF',
    border: '1px solid #B3D9F7',
    borderRadius: 12,
    padding: '16px 20px',
    color: '#0D4C8B',
    fontSize: 14,
    textAlign: 'center',
  },
  loginLink: {
    color: '#0288D1',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
};

const AiresumeAnalyzer = () => {
  const navigate = useNavigate();
  const [isAnalyzerOpen, setIsAnalyzerOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [improving, setImproving] = useState(false);
  const [improvedText, setImprovedText] = useState('');
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');
  const isLoggedIn = !!token;

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes glowPulse {
        0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
        50% { transform: translate(-50%, -50%) scale(1.5); opacity: 1; }
      }
      .ai-back-btn:hover { transform: translateX(-4px); box-shadow: 0 6px 24px rgba(0,43,91,0.35); }
      .ai-open-btn:hover { transform: scale(1.01); box-shadow: 0 6px 24px rgba(0,43,91,0.3); }
      .ai-history-btn:hover { background: #B3D9F7; }
      .ai-history-item:hover { background: #EFF7FF; transform: translateX(4px); }
      .ai-analyze-btn:hover { transform: scale(1.02); box-shadow: 0 6px 24px rgba(0,43,91,0.35); }
      .ai-card-hover:hover { border-color: #0288D1; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const fetchHistory = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const res = await axios.get(`${getResumeApiUrl()}/resume/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Your login session expired. Please login again to view history.');
      }
    }
  }, [token, isLoggedIn]);

  useEffect(() => {
    if (isAnalyzerOpen && isLoggedIn) fetchHistory();
  }, [isAnalyzerOpen, isLoggedIn, fetchHistory]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles?.length > 0) {
      setFile(acceptedFiles[0]);
      setAnalysis(null);
      setImprovedText('');
      setError('');
    }
  }, []);

  const onDropRejected = useCallback(() => {
    setError('Only PDF and DOCX resume files are allowed. Maximum file size is 5 MB.');
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
  });

  const handleUpload = async () => {
    if (!file) return setError('Please select a PDF or DOCX resume first.');
    
    setLoading(true);
    setError('');
    const formData = new FormData();
    formData.append('resume', file);
    
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const res = await axios.post(`${getResumeApiUrl()}/resume/analyze`, formData, { headers });
      setAnalysis({ ...res.data.analysis, fileName: file.name });
      
      if (isLoggedIn) {
        await fetchHistory();
      }
    } catch (err) {
      setError('Analysis failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const improveResume = async () => {
    if (!analysis) return;
    if (!analysis.extractedText) return setError('Resume text is not available. Please upload and analyze again.');
    
    setImproving(true);
    setError('');
    try {
      const headers = {};
      if (isLoggedIn) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const res = await axios.post(
        `${getResumeApiUrl()}/resume/improve`,
        { originalText: analysis.extractedText, suggestions: analysis.suggestions },
        { headers }
      );
      setImprovedText(res.data.improvedText);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Unauthorized. Please login again to improve your resume.');
      } else {
        setError('Improvement failed: ' + (err.response?.data?.error || err.message));
      }
    } finally {
      setImproving(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const toggleAnalyzer = () => {
    setIsAnalyzerOpen((p) => !p);
    setShowHistory(false);
    setError('');
  };

  const scoreColor = (s) => s >= 75 ? '#22c55e' : s >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div style={styles.page}>
      <div style={styles.glowEffect} />
      <div style={styles.glowEffectLeft} />

      <div style={styles.container}>

        {/* Back Button */}
        <button type="button" className="ai-back-btn" style={styles.backBtn} onClick={handleBack}>
          ← Back to Home
        </button>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.badge}>⚡Scan. Score. Succeed.</div>
          <h1 style={styles.title}>
            Resume <span style={{ color: '#0288D1' }}>Genius</span> Analyzer
          </h1>
          <p style={styles.subtitle}>
            Unlock your career potential with AI-driven resume insights — score, optimize, and stand out instantly.
          </p>

          {/* Project Name */}
          <div style={styles.projectName}>
            <div style={styles.projectNameGlow} />
            <span style={styles.projectNameHighlight}>Recruiters scroll fast — make them stop at yours</span>
          </div>
        </div>

        {/* Open / Close card */}
        <div style={styles.card} className="ai-card-hover">
          <div style={styles.flexBetween}>
            <div style={{ flex: 1 }}>
              <button type="button" className="ai-open-btn" style={styles.openBtn} onClick={toggleAnalyzer}>
                {isAnalyzerOpen ? '✕  Close Analyzer' : '⚡  Open AI Resume Analyzer'}
              </button>
              <p style={styles.openBtnHint}>
                Upload PDF or DOCX · Instant ATS Score · Role Matching · AI Suggestions
              </p>
            </div>
            {isAnalyzerOpen && isLoggedIn && (
              <button type="button" className="ai-history-btn" style={styles.historyBtn} onClick={() => setShowHistory((p) => !p)}>
                {showHistory ? '← Back' : '🕓 History'}
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && <div style={styles.errorBox}>⚠️ {error}</div>}

        {/* Content */}
        {isAnalyzerOpen && (
          showHistory ? (

            // History View (only when logged in)
            <div style={styles.card} className="ai-card-hover">
              <h3 style={styles.sectionTitle()}>🕓 Previous Analyses</h3>
              {history.length === 0 ? (
                <p style={{ color: '#0D4C8B', fontSize: 14, margin: 0 }}>
                  No analyses yet. Upload a resume to get started.
                </p>
              ) : (
                history.map((item) => (
                  <button
                    key={item._id}
                    type="button"
                    className="ai-history-item"
                    style={styles.historyItem}
                    onClick={() => { setAnalysis(item); setShowHistory(false); setError(''); }}
                  >
                    <p style={styles.historyName}>📄 {item.fileName}</p>
                    <p style={styles.historyMeta}>ATS Score: {item.atsScore}/100</p>
                    <p style={styles.historyDate}>{new Date(item.createdAt).toLocaleDateString()}</p>
                  </button>
                ))
              )}
            </div>

          ) : (
            <>
              {/* Upload Card */}
              <div style={styles.card} className="ai-card-hover">
                <div style={styles.uploadInfo}>
                  <p style={styles.uploadInfoTitle}>📁 Upload Your Resume</p>
                  <p style={styles.uploadInfoText}>
                    Drag &amp; drop or click to select a PDF or DOCX file (max 5 MB).
                  </p>
                  {!isLoggedIn && (
                    <div style={{ ...styles.loginPrompt, marginTop: 12 }}>
                      ℹ️ You are using AI Analyzer as a guest. 
                      <span style={styles.loginLink} onClick={() => window.location.href = '/signup'}> Login </span> 
                      to save your analysis history.
                    </div>
                  )}
                </div>

                <div {...getRootProps()} style={styles.dropzone(isDragActive)}>
                  <input {...getInputProps()} />
                  <div style={styles.dropIcon}>{isDragActive ? '📂' : '📄'}</div>
                  <p style={styles.dropText}>
                    {isDragActive ? 'Drop your resume here…' : 'Drag & drop or click to select'}
                  </p>
                  <p style={styles.dropSub}>Supported: PDF, DOCX · Max size: 5 MB</p>
                  {file && <span style={styles.dropFile}>✅ {file.name}</span>}
                </div>

                <button
                  type="button"
                  className="ai-analyze-btn"
                  onClick={handleUpload}
                  disabled={!file || loading}
                  style={styles.analyzeBtn(!file || loading)}
                >
                  {loading ? '⏳ Analyzing…' : '🔍 Analyze Resume'}
                </button>

                {loading && (
                  <div style={styles.spinnerWrap}>
                    <div style={styles.spinner} />
                    <span style={styles.spinnerText}>Scanning your resume…</span>
                  </div>
                )}
              </div>

              {/* Results */}
              {analysis && (
                <div id="report-content">

                  {/* ATS Score */}
                  <div style={styles.card} className="ai-card-hover">
                    <h3 style={styles.sectionTitle()}>📊 ATS Score</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 12 }}>
                      <div style={{ ...styles.atsNumber, color: scoreColor(analysis.atsScore) }}>
                        {analysis.atsScore}
                        <span style={{ fontSize: 22, fontWeight: 600, color: '#0D4C8B' }}>/100</span>
                      </div>
                      <div style={styles.progressTrack}>
                        <div style={{ ...styles.progressFill(analysis.atsScore), background: scoreColor(analysis.atsScore) }} />
                      </div>
                    </div>
                    <p style={{ color: '#0D4C8B', fontSize: 13, margin: 0 }}>
                      Based on keyword matching, formatting quality, and content depth.
                    </p>
                  </div>

                  {/* Missing Skills + Matching Roles */}
                  <div style={styles.grid2}>
                    <div style={styles.card} className="ai-card-hover">
                      <h3 style={styles.sectionTitle('#e53e3e')}>⚠️ Missing Skills</h3>
                      {analysis.missingSkills?.length ? (
                        <div>
                          {analysis.missingSkills.map((skill, i) => (
                            <span key={i} style={styles.tag('#e53e3e', '#fff5f5')}>{skill}</span>
                          ))}
                        </div>
                      ) : (
                        <p style={{ color: '#0D4C8B', fontSize: 13, margin: 0 }}>No missing skills detected.</p>
                      )}
                    </div>

                    <div style={styles.card} className="ai-card-hover">
                      <h3 style={styles.sectionTitle()}>🎯 Best Matching Roles</h3>
                      {analysis.matchingRoles?.map((role, i) => (
                        <div key={i} style={styles.roleRow}>
                          <div style={styles.roleLabel}>
                            <span>{role.role}</span>
                            <span style={styles.rolePct}>{role.matchPercentage}%</span>
                          </div>
                          <div style={styles.roleTrack}>
                            <div style={styles.roleFill(role.matchPercentage)} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Suggestions */}
                  <div style={styles.card} className="ai-card-hover">
                    <h3 style={styles.sectionTitle('#0288D1')}>💡 Improvement Suggestions</h3>
                    {analysis.suggestions?.length ? (
                      <ul style={styles.ul}>
                        {analysis.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    ) : (
                      <p style={{ color: '#0D4C8B', fontSize: 13, margin: 0 }}>No suggestions available.</p>
                    )}
                  </div>

                  {/* Strengths + Weaknesses */}
                  <div style={styles.grid2}>
                    <div style={styles.card} className="ai-card-hover">
                      <h3 style={styles.sectionTitle('#16a34a')}>✅ Strengths</h3>
                      <ul style={styles.ul}>
                        {analysis.strengths?.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                    <div style={styles.card} className="ai-card-hover">
                      <h3 style={styles.sectionTitle('#e53e3e')}>❌ Weaknesses</h3>
                      <ul style={styles.ul}>
                        {analysis.weaknesses?.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </div>
                  </div>

                  {/* Grammar */}
                  {analysis.grammarCorrections?.length > 0 && (
                    <div style={styles.card} className="ai-card-hover">
                      <h3 style={styles.sectionTitle('#0288D1')}>✏️ Grammar &amp; Spelling</h3>
                      <ul style={styles.ul}>
                        {analysis.grammarCorrections.map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* Found Skills */}
                  {analysis.skillsFound?.length > 0 && (
                    <div style={styles.card} className="ai-card-hover">
                      <h3 style={styles.sectionTitle('#16a34a')}>🛠️ Skills Found in Resume</h3>
                      <div>
                        {analysis.skillsFound.map((s, i) => (
                          <span key={i} style={styles.tag('#16a34a', '#f0fdf4')}>{s}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Improved Text */}
                  {improvedText && (
                    <div style={{ ...styles.card, ...styles.improvedBox }} className="ai-card-hover">
                      <h3 style={styles.sectionTitle('#16a34a')}>✨ AI Improved Resume</h3>
                      <pre style={styles.pre}>{improvedText}</pre>
                    </div>
                  )}

                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
};
 
export default AiresumeAnalyzer;